import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, MapPin, DollarSign, Upload, FileText } from "lucide-react";

const SAMPLE = `Recent high school grad. Comfortable working outdoors and with my hands. Some experience helping my uncle rewire a garage — basic electrical work. Good at math, physically fit, willing to travel. Interested in learning a skilled trade that pays well.`;

export default function AIMatch() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [mode, setMode] = useState("text"); // 'text' | 'pdf'
  const [file, setFile] = useState(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "application/pdf": [".pdf"] },
    maxSize: 5 * 1024 * 1024,
    multiple: false,
    onDrop: (files) => { if (files[0]) setFile(files[0]); },
    onDropRejected: () => toast.error("Please drop a PDF under 5MB"),
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMatches([]);
    try {
      let res;
      if (mode === "pdf") {
        if (!file) { toast.error("Please choose a PDF"); setLoading(false); return; }
        const form = new FormData();
        form.append("file", file);
        res = await api.post("/ai/match-upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        if (text.trim().length < 20) { toast.error("Add at least 20 characters about your skills."); setLoading(false); return; }
        res = await api.post("/ai/match", { resume_text: text });
      }
      setMatches(res.data.matches || []);
      if (!res.data.matches?.length) toast.error("No matches found. Try adding more detail.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI matching failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2 flex items-center gap-2">
        <Sparkles size={14} /> AI-Powered Career Matching
      </div>
      <h1 className="font-display text-5xl md:text-6xl uppercase leading-none mb-4">
        Find your <span className="text-yellow-500">match</span>
      </h1>
      <p className="text-zinc-400 max-w-2xl leading-relaxed">
        Drop your resume or paste your skills. Claude Sonnet 4.5 matches you to the top 5 apprenticeships with reasoning.
      </p>

      <div className="mt-8 inline-flex border border-zinc-800">
        <button
          data-testid="ai-mode-text"
          onClick={() => setMode("text")}
          className={`px-6 py-2 uppercase tracking-wider text-xs font-bold ${mode === "text" ? "bg-yellow-500 text-black" : "text-zinc-400 hover:text-white"}`}
        >
          Paste Text
        </button>
        <button
          data-testid="ai-mode-pdf"
          onClick={() => setMode("pdf")}
          className={`px-6 py-2 uppercase tracking-wider text-xs font-bold ${mode === "pdf" ? "bg-yellow-500 text-black" : "text-zinc-400 hover:text-white"}`}
        >
          Upload PDF
        </button>
      </div>

      <form onSubmit={submit} className="mt-6">
        {mode === "text" ? (
          <div className="relative border border-zinc-800 focus-within:border-yellow-500 bg-zinc-950 overflow-hidden">
            {loading && <div className="scan-line" />}
            <textarea
              data-testid="ai-match-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. High school grad, comfortable with tools, love working outdoors, interested in electrical work…"
              rows={8}
              className="w-full bg-transparent p-4 text-white placeholder:text-zinc-600 focus:outline-none font-mono-alt text-sm resize-none"
            />
          </div>
        ) : (
          <div
            {...getRootProps()}
            data-testid="ai-pdf-dropzone"
            className={`relative border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragActive ? "border-yellow-500 bg-yellow-500/5" : "border-zinc-800 bg-zinc-950 hover:border-yellow-500/60"}`}
          >
            {loading && <div className="scan-line" />}
            <input {...getInputProps()} />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText size={28} className="text-yellow-500" />
                <div className="text-left">
                  <div className="text-white font-bold uppercase text-sm">{file.name}</div>
                  <div className="text-zinc-500 text-xs">{(file.size / 1024).toFixed(1)} KB &middot; click to change</div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Upload size={32} className="text-yellow-500" />
                <div className="font-display uppercase text-2xl leading-none">Drop your resume PDF</div>
                <div className="text-zinc-500 text-sm">or click to browse &middot; max 5MB</div>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-4">
          {mode === "text" ? (
            <button data-testid="ai-match-sample" type="button" onClick={() => setText(SAMPLE)} className="text-xs uppercase tracking-wider text-zinc-500 hover:text-yellow-500">Try sample input</button>
          ) : <span />}
          <button
            data-testid="ai-match-submit"
            disabled={loading}
            className="bg-yellow-500 text-black font-bold uppercase tracking-wider px-8 py-3 hover:bg-yellow-400 active:scale-95 transition flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Scanning</> : <>Match Me <ArrowRight size={16} /></>}
          </button>
        </div>
      </form>

      {matches.length > 0 && (
        <div className="mt-14">
          <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2">Your Top 5 Matches</div>
          <h2 className="font-display text-4xl uppercase leading-none mb-6">Handpicked for <span className="text-yellow-500">you</span></h2>
          <div className="space-y-3">
            {matches.map((m, i) => (
              <div key={m.job.id} className="border border-zinc-800 bg-zinc-950/50 p-6 relative" style={{ animation: `fadeUp 0.5s ease ${i * 60}ms both` }}>
                <div className="absolute top-0 right-0 bg-yellow-500 text-black font-mono-alt font-bold text-sm px-3 py-1">
                  {m.match_score}% MATCH
                </div>
                <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest">{m.job.industry}</div>
                <Link to={`/jobs/${m.job.id}`} className="font-display text-3xl uppercase leading-none hover:text-yellow-500 block mt-1">{m.job.title}</Link>
                <div className="text-zinc-400 text-sm mt-1">{m.job.employer}</div>
                <div className="flex gap-4 text-xs text-zinc-500 mt-3">
                  <span className="flex items-center gap-1"><MapPin size={12} className="text-yellow-500" />{m.job.location}</span>
                  <span className="flex items-center gap-1"><DollarSign size={12} className="text-yellow-500" />${m.job.wage_min}-${m.job.wage_max}/hr</span>
                </div>
                <div className="mt-4 border-l-2 border-yellow-500 pl-3 text-sm text-zinc-300">
                  <span className="text-yellow-500 font-mono-alt uppercase text-xs tracking-widest">Why: </span>
                  {m.reasoning}
                </div>
                <Link to={`/jobs/${m.job.id}`} data-testid={`ai-view-${m.job.id}`} className="mt-4 inline-flex items-center gap-1 text-yellow-500 hover:text-yellow-400 uppercase text-xs font-bold tracking-wider">
                  View job <ArrowRight size={12} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
